import { useGetAllTeamAppraisalQuery } from "@/app/features/employee/performance/performance.api";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { FILTER_KEYS } from "@/constants/keys.constants";
import { useAppFilters } from "@/hooks";
import useAuth from "@/hooks/useAuth";
import useDiscloser from "@/hooks/useDiscloser";
import { EyeIcon, LaptopPerformanceIcon } from "@/icons";
import { formatDate, normalizeColumnValue } from "@/utils";
import { countParPageSerialNumber } from "@/utils/global.utils";
import { Button, Flex, type TableProps } from "antd";
import LogViewer from "./LogViewer";
import ManageTeamAppraisal from "./ManageTeamAppraisal";
import ViewAppraisal from "./ViewAppraisal";

const APPRAISAL_STATUS_CONFIG = [
  {
    label: "Not Started",
    value: "NOT_STARTED",
    className: "status status-slate",
  },
  {
    label: "Draft",
    value: "DRAFT",
    className: "status status-fuchsia",
  },
  {
    label: "Pending",
    value: "PENDING",
    className: "status status-yellow",
  },
  {
    label: "Submitted",
    value: "SUBMITTED",
    className: "status status-sky",
  },
  {
    label: "Approved",
    value: "APPROVED",
    className: "status status-success",
  },
  {
    label: "Rejected",
    value: "REJECTED",
    className: "status status-error",
  },
];

const TeamAppraisal = () => {
  const { userId, organizationId } = useAuth();
  const { page, limit, handlePagination, filters } = useAppFilters(
    FILTER_KEYS.LM_TEAM_APPRAISAL,
  );
  const [config, handleOpen, handleClose] = useDiscloser<
    "MANAGE" | "VIEW" | "LOG"
  >();

  const { data, isFetching } = useGetAllTeamAppraisalQuery({
    organization_id: organizationId,
    manager_id: userId,
    page,
    limit,
    ...filters,
  });

  const appraisals = data?.data?.employees || [];
  const total = data?.data?.pagination?.total || 0;

  const columns: TableProps<any>["columns"] = [
    {
      title: "SL. No.",
      align: "center",
      width: 80,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Employee ID",
      dataIndex: ["employee", "employee_id"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Employee Name",
      dataIndex: ["employee", "name"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Department",
      dataIndex: ["employee", "department"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Appraisal Status",
      dataIndex: "appraisal_approval_status",
      align: "center",
      render: (val: string) => (
        <StatusTag
          value={val}
          shouldCapitalize={false}
          config={APPRAISAL_STATUS_CONFIG}
        />
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",
      render: (_, record) => (
        <Flex justify="center" gap={8}>
          <Button
            size="small"
            className="action-btn"
            icon={<EyeIcon size={20} />}
            disabled={record.appraisal_approval_status === "NOT_STARTED"}
            onClick={() => {
              if (
                record.appraisal_approval_status === "PENDING" ||
                record.appraisal_approval_status === "DRAFT"
              ) {
                handleOpen({
                  type: "MANAGE",
                  data: {
                    planName: record.plan_name,
                    mappingId: record.mapping_id,
                    planId: record.plan_id,
                    employeeId: record.employee_id,
                    appraisalStatus: record.appraisal_approval_status,
                  },
                });
              } else {
                handleOpen({
                  type: "VIEW",
                  data: {
                    planName: record.plan_name,
                    mappingId: record.mapping_id,
                  },
                });
              }
            }}
          />
          <Button
            size="small"
            className="btn-primary-bordered h-8 flex items-center gap-2"
            onClick={() => {
              handleOpen({
                type: "LOG",
                data: {
                  planName: record.plan_name,
                  mappingId: record.mapping_id,
                  dateTime: record.created_at
                    ? formatDate(record.created_at)
                    : "",
                },
              });
            }}
          >
            <LaptopPerformanceIcon className="text-theme-primary size-4" /> Log
            View
          </Button>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={appraisals}
        onPaginate={handlePagination}
        customTitle="Team Member Appraisal List"
        tableRootClassName="border-0 p-0"
        loading={isFetching}
        total={total}
        filterSection={<></>}
      />

      <ViewAppraisal
        open={config.open && config.type === "VIEW"}
        onClose={handleClose}
        planName={config.data?.planName}
        mappingId={config.data?.mappingId}
        source="TEAM"
      />

      <ManageTeamAppraisal
        open={config.open && config.type === "MANAGE"}
        onClose={handleClose}
        planName={config.data?.planName}
        mappingId={config.data?.mappingId}
        planId={config.data?.planId}
        employeeId={config.data?.employeeId}
        appraisalStatus={config.data?.appraisalStatus}
      />

      <LogViewer
        open={config.open && config.type === "LOG"}
        onClose={handleClose}
        id={config.data?.mappingId}
        planName={config.data?.planName}
        dateTime={config.data?.dateTime}
      />
    </>
  );
};

export default TeamAppraisal;
